import { EXPRESS_SLA_ID, IAvailableWindowWithExpress } from '../../types'

const OPERATIONAL_TIME_ZONE = 'America/Santiago'

const getOperationalDateTime = (date: Date): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone: OPERATIONAL_TIME_ZONE,
    year: 'numeric'
  })
    .formatToParts(date)
    .reduce<Record<string, string>>((values, part) => {
      values[part.type] = part.value
      return values
    }, {})

  const hour = parts.hour === '24' ? '00' : parts.hour

  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}:${parts.second}`
}

const getWindowOperationalDateTime = (startDateUtc?: string): string => {
  return startDateUtc?.slice(0, 19) ?? ''
}

/**
 * Retrieves scheduled windows for a specific day and its closest future Express window.
 * @param {IAvailableWindows[]} availableWindows - An array of available windows.
 * @param {string} selectedDay - The day selected earlier.
 * @returns {Array<{startDate: string, endDate: string}>} - Express first when it belongs to the selected day, followed by scheduled windows.
 * @example
 * const availableWindows = [
 *   { startDateUtc: "2024-02-29T15:00:00+00:00", endDateUtc: "2024-02-29T17:00:59+00:00", price: 2990 },
 *   { startDateUtc: "2024-02-29T17:00:00+00:00", endDateUtc: "2024-02-29T19:00:59+00:00", price: 2990 },
 * ];
 * const selectedDay = "2024-02-29T15:00:00";
 * const datesForSelectedDay = getTimesForSelectedDay(availableWindows, selectedDay);
 * Output: [{ startDate: "2024-02-29T15:00:00", endDate: "2024-02-29T17:00:59" }, { startDate: "2024-02-29T17:00:00", endDate: "2024-02-29T19:00:59" }]
 */
export const getTimesForSelectedDay = (
  availableWindows: IAvailableWindowWithExpress[],
  selectedDay: string,
  now = new Date()
): IAvailableWindowWithExpress[] => {
  const dateString = selectedDay.split('T')[0]
  const currentOperationalDateTime = getOperationalDateTime(now)
  const scheduledWindows = availableWindows
    .filter((window) => {
      return (
        window?.id !== EXPRESS_SLA_ID &&
        window?.startDateUtc?.startsWith(dateString)
      )
    })
    .sort((firstWindow, secondWindow) => {
      return getWindowOperationalDateTime(
        firstWindow?.startDateUtc
      ).localeCompare(getWindowOperationalDateTime(secondWindow?.startDateUtc))
    })
  const closestExpressWindow = availableWindows
    .filter((window) => {
      const startDate = getWindowOperationalDateTime(window?.startDateUtc)

      return (
        window?.id === EXPRESS_SLA_ID &&
        startDate !== '' &&
        startDate >= currentOperationalDateTime
      )
    })
    .sort((firstWindow, secondWindow) => {
      return getWindowOperationalDateTime(
        firstWindow?.startDateUtc
      ).localeCompare(getWindowOperationalDateTime(secondWindow?.startDateUtc))
    })[0]

  if (closestExpressWindow?.startDateUtc?.startsWith(dateString)) {
    return [closestExpressWindow, ...scheduledWindows]
  }

  return scheduledWindows
}
